import { formatWhen } from '../../lib/display-time'
/**
 * Carrier tracking links and shipment labels (procurement.md §3). Staff-only UI: carrier names are fine here; customer
 * renderers never see them (hard rule 1), and nothing in this module feeds a customer message.
 */
import type { Carrier, ShipmentOut, ShipmentStatus } from './api'

const TRACK: Record<Exclude<Carrier, 'other'>, (n: string) => string> = {
  ups: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  purolator: (n) => `https://www.purolator.com/en/shipping/tracker?pin=${encodeURIComponent(n)}`,
  canadapost: (n) => `https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${encodeURIComponent(n)}`,
}

export const CARRIER_LABEL: Record<Carrier, string> = {
  ups: 'UPS',
  fedex: 'FedEx',
  purolator: 'Purolator',
  canadapost: 'Canada Post',
  other: 'Other carrier',
}

export const SHIPMENT_LABEL: Record<ShipmentStatus, string> = {
  label_created: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  unknown: 'Status unknown',
}

/** The API's link if it is an https URL, else the carrier's own public tracker; null for an unknown carrier. */
export function trackingUrl(s: Pick<ShipmentOut, 'carrier' | 'tracking_number' | 'tracking_url'>): string | null {
  if (s.tracking_url && /^https:\/\//i.test(s.tracking_url)) return s.tracking_url
  if (s.carrier === 'other') return null
  return TRACK[s.carrier](s.tracking_number)
}

/** Shown while Ship24 is not configured, in place of a stale "Label created". */
export const UNTRACKED_STATUS = 'Status not tracked automatically, check the link'

export const SITE_SERIALS_PENDING = 'Delivered, serials pending'
export const OFFICE_NOT_CHECKED_IN = 'delivered to office, not yet checked in'

const COMPACT = (raw: string) => raw.replace(/\s+/g, '').toUpperCase()

/** 12 digits are FedEx and Purolator; 15- and 20-digit numbers overlap too. No guess (#1104). */
export function needsCarrierChoice(raw: string): boolean {
  const n = COMPACT(raw)
  return /^\d{12}$/.test(n) || /^\d{15}$/.test(n) || /^\d{20}$/.test(n)
}

/** Best-effort carrier guess for a pasted number; the API's per-carrier regexes are authoritative. */
export function guessCarrier(raw: string): Carrier | null {
  const n = COMPACT(raw)
  if (needsCarrierChoice(n)) return null
  if (/^1Z[0-9A-Z]{16}$/.test(n)) return 'ups'
  if (/^[A-Z]{2}\d{9}CA$/.test(n) || /^\d{16}$/.test(n)) return 'canadapost'
  if (/^[A-Z]{3}\d{9}$/.test(n)) return 'purolator'
  return null
}

const CA_POSTAL = /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d\b/i
const CA_WORD =
  /\b(canada|canadian|alberta|british columbia|manitoba|new brunswick|newfoundland|nova scotia|ontario|prince edward island|quebec|québec|saskatchewan|yukon|northwest territories|nunavut)\b/i

/** Purolator is suggested first for a Canadian vendor or a Canadian ship-to. Ewee's office counts. */
export function suggestsPurolatorFirst(order: {
  vendor_name?: string | null
  ship_to?: { kind?: string; address?: string | null }
}): boolean {
  const address = order.ship_to?.address ?? ''
  if (CA_POSTAL.test(address) || CA_WORD.test(address) || CA_WORD.test(order.vendor_name ?? '')) return true
  if (/\b(united states|u\.s\.a\.?|usa)\b/i.test(address)) return false
  return order.ship_to?.kind === 'office'
}

export function orderedCarriers(preferPurolator: boolean, ambiguous: boolean): Carrier[] {
  const all = Object.keys(CARRIER_LABEL) as Carrier[]
  if (!ambiguous) return all
  const rest = all.filter((carrier) => carrier !== 'fedex' && carrier !== 'purolator')
  return preferPurolator ? ['purolator', 'fedex', ...rest] : ['fedex', 'purolator', ...rest]
}

export function carrierPrompt(preferPurolator: boolean, ambiguous: boolean, guessed: Carrier | null): string {
  if (ambiguous) return preferPurolator ? 'Purolator or FedEx?' : 'FedEx or Purolator?'
  if (guessed) return `${CARRIER_LABEL[guessed]} (guessed)`
  return 'Carrier: detect'
}

export function shipmentStatusText(shipment: { status: ShipmentStatus; tracking_automatic?: boolean | null }): string {
  if (shipment.tracking_automatic !== true && shipment.status === 'label_created') return UNTRACKED_STATUS
  return SHIPMENT_LABEL[shipment.status]
}

/** UTC stamp matching the API's delivery note (`YYYY-MM-DD HH:MM UTC`). */
export function carrierDeliveredPhrase(iso: string | null | undefined): string {
  if (!iso) return 'received on site (carrier delivered time not recorded)'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'received on site (carrier delivered time not recorded)'
  const pad = (value: number) => String(value).padStart(2, '0')
  const stamp = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`
  return `received on site (carrier delivered ${stamp})`
}

export function lineDelivery(
  order: {
    ship_to: { kind: 'office' | 'customer_site' }
    shipments: Array<{
      status: ShipmentStatus
      destination?: 'office' | 'customer_site'
      leg_index?: number
      last_event_at?: string | null
    }>
  },
  line: { qty: number; qty_received: number },
): { primary: string; detail: string | null } {
  const count = `Received ${line.qty_received}/${line.qty}`
  if (line.qty_received >= line.qty) return { primary: count, detail: null }
  const final = [...order.shipments].sort((a, b) => (a.leg_index ?? 0) - (b.leg_index ?? 0)).at(-1)
  // A partial check-in keeps its count. The carrier phrase replaces 0/1 only (#1104).
  if (final?.status !== 'delivered' || line.qty_received > 0) return { primary: count, detail: null }
  if ((final.destination ?? order.ship_to.kind) === 'customer_site') {
    return { primary: SITE_SERIALS_PENDING, detail: carrierDeliveredPhrase(final.last_event_at ?? null) }
  }
  return { primary: OFFICE_NOT_CHECKED_IN, detail: null }
}

/** #1190: the add form follows the order first, then defaults office staging to the customer leg. */
export function nextShipmentDestination(order: {
  ship_to: { kind: 'office' | 'customer_site' }
  shipments: Array<{ destination?: 'office' | 'customer_site'; status: ShipmentStatus; leg_index?: number }>
}): 'office' | 'customer_site' {
  const final = [...order.shipments].sort((a, b) => (a.leg_index ?? 0) - (b.leg_index ?? 0)).at(-1)
  if (!final) return order.ship_to.kind
  const destination = final.destination ?? order.ship_to.kind
  return destination === 'office' && final.status === 'delivered' ? 'customer_site' : destination
}

/** "Oct 21" in the viewer's zone; ETAs are dates (YYYY-MM-DD) or timestamps. */
export function formatEta(eta: string | null | undefined): string | null {
  if (!eta) return null
  const d = /^\d{4}-\d{2}-\d{2}$/.test(eta) ? new Date(`${eta}T12:00:00`) : new Date(eta)
  if (Number.isNaN(d.getTime())) return eta
  return formatWhen(d.toISOString(), { style: 'date' })
}
